import type React from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '@src/i18n'
import { generatePath } from 'react-router-dom'

import routes from '@src/Routes/routes'
import { CALL_LINK, GUIDES_LINK, YOUTUBE_PLAYLISTS } from '@src/constants'
import { SUPPORT_EMAIL } from '@msteams/constants'
import { isIntercomAvailable, safeIntercom } from '@helpers/Intercom/safe'
import {
  IconCircleCheck,
  IconListDetails,
  IconBolt,
  IconBuildingWarehouse,
  IconUsers,
  IconHome,
  IconUsersGroup,
  IconReceipt,
  IconTruckDelivery,
  IconFileInvoice,
  IconCoins,
  IconMail,
  IconFileDollar,
  IconChartInfographic,
  IconNotes,
  IconFileDescription,
  IconSparkles,
  IconPlug,
} from '@tabler/icons-react'

import { ContactParty } from '@models/ApproveitAccounting'
import type { UIFeatures } from '@models/Account'
import type { Role } from '@type/common'
import { Perm } from '@type/permissions'
import type { PermCodename } from '@type/permissions'
import { Feature } from '@type/features'

export interface NavItem {
  to?: string
  label: string
  icon: React.FC<Record<string, unknown>>
  /**
   * Legacy role gate. Kept for backward compat during the RBAC
   * migration but new items SHOULD use `requirePerm` instead.
   * When both are set, `requirePerm` wins.
   */
  show?: Role[]
  /**
   * RBAC permission codename required to see this item. Checked via
   * the user's resolved permissions (multi-role aware). Use this
   * instead of `show` for new items, and migrate existing items
   * domain-by-domain.
   */
  requirePerm?: PermCodename
  /**
   * RBAC: show this item if the user holds ANY of these codenames.
   * For container entries (e.g. Team) whose child pages are each gated
   * by a different perm — a single `requirePerm` would hide the entry
   * from a user who can reach a sibling tab. Takes precedence over
   * `requirePerm` / `show` when set.
   */
  requireAnyPerm?: PermCodename[]
  external?: boolean
  onClick?: () => void
  feature?: Feature
  isCollapsed?: boolean
  disabled?: boolean
  // Non-plan lock (e.g. beta gating): keeps the item visible and clickable,
  // renders a ContentLock badge, and lets the destination page do the pitch.
  locked?: boolean
  lockTooltip?: string
  // Hidden unless the team's mcp_actions_enabled beta flag is on (ADN-484).
  requiresMcpActions?: boolean
  /**
   * Workspace feature toggle (Settings → Features). Checked BEFORE the
   * RBAC perm gates: a feature hidden for the viewer's axis (admin/user)
   * never shows, regardless of perms; an enabled one still requires
   * `requirePerm` / `requireAnyPerm` to pass. Cosmetic only — endpoint
   * access stays RBAC-governed.
   */
  uiFeatureKey?: keyof UIFeatures
}

// LEFT ITEMS

export const NAV_ITEMS: NavItem[] = [
  {
    to: routes.private.dashboard,
    icon: IconHome,
    label: i18n.t('common:navigation.dashboard'),
    // Dashboard's content is approval-request-centric (stats, recent
    // activity). Roles without REQUEST_VIEW have nothing to render and
    // saw an empty page; gating the nav item here pairs with the smart
    // landing redirect so they're routed to their actual work surface.
    requirePerm: Perm.REQUEST_VIEW,
  },
  {
    to: routes.private.reports,
    icon: IconChartInfographic,
    label: i18n.t('common:navigation.reports'),
    requirePerm: Perm.REPORT_VIEW,
    uiFeatureKey: 'reports',
  },
  {
    to: routes.private.assistant,
    icon: IconSparkles,
    label: i18n.t('common:navigation.aiAssistant'),
  },
  {
    to: routes.private.pipelines,
    icon: IconBolt,
    label: i18n.t('common:navigation.workflows'),
    // A container entry: Custom lists is a TAB of this section with its own
    // codename, so gating on workflow.view alone hid the whole section from
    // a role granted only custom_list.view — the page stayed reachable by
    // typing the URL, but nothing in the app led there.
    requireAnyPerm: [Perm.WORKFLOW_VIEW, Perm.CUSTOM_LIST_VIEW],
    uiFeatureKey: 'workflows',
  },
  {
    to: routes.private.approvalRequests,
    icon: IconCircleCheck,
    label: i18n.t('common:navigation.approvalRequests'),
    requirePerm: Perm.REQUEST_VIEW,
  },
  {
    to: routes.private.actions,
    icon: IconPlug,
    label: i18n.t('common:navigation.actions'),
    requiresMcpActions: true,
    // Every other nav item carries a permission. Without one this was the
    // only ungated entry, and getLandingRoute picks the FIRST item that
    // passes the filter — so a member whose permissions hid everything else
    // landed on /actions after login. Actions are attached to approval
    // requests, so seeing them follows request.view.
    requirePerm: Perm.REQUEST_VIEW,
  },
  {
    to: routes.private.tasks,
    icon: IconListDetails,
    label: i18n.t('common:navigation.tasks'),
    requirePerm: Perm.TASK_VIEW,
    uiFeatureKey: 'tasks',
  },
  {
    to: routes.private.mailbox,
    icon: IconMail,
    label: i18n.t('common:navigation.mailbox'),
    feature: Feature.mailbox,
    requirePerm: Perm.MAILBOX_VIEW,
    uiFeatureKey: 'mailbox',
  },
  {
    to: routes.private.organizationalStructure,
    icon: IconUsersGroup,
    label: i18n.t('common:navigation.team'),
    // The Team area is a container: Members (team.view), Departments /
    // Locations (team.manage_org_structure), Roles (role.view). Show the
    // entry if the user can reach ANY tab — otherwise a role-admin
    // without team.view can't get to Roles except by typing the URL.
    // The /members route (TeamHome) then lands them on the first tab
    // they can actually open.
    requireAnyPerm: [Perm.TEAM_VIEW, Perm.TEAM_MANAGE_ORG_STRUCTURE, Perm.ROLE_VIEW],
    uiFeatureKey: 'team',
  },
  {
    to: routes.private.costCenter,
    icon: IconCoins,
    label: i18n.t('common:navigation.costCenters'),
    feature: Feature.costCenters,
    requirePerm: Perm.COST_CENTER_VIEW,
    uiFeatureKey: 'cost_centers',
  },
]

export const AR_ITEMS: NavItem[] = [
  {
    to: routes.private.invoices,
    icon: IconFileInvoice,
    label: i18n.t('common:navigation.invoices'),
    requirePerm: Perm.INVOICE_VIEW,
    uiFeatureKey: 'invoices',
  },
  {
    to: routes.private.quotes,
    icon: IconFileDollar,
    label: i18n.t('common:navigation.quotes'),
    requirePerm: Perm.QUOTE_VIEW,
    uiFeatureKey: 'quotes',
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.customer }),
    icon: IconUsers,
    label: i18n.t('common:navigation.customers'),
    requirePerm: Perm.CUSTOMER_VIEW,
    uiFeatureKey: 'customers',
  },
]
export const AP_ITEMS: NavItem[] = [
  {
    to: routes.private.bills,
    icon: IconReceipt,
    label: i18n.t('common:navigation.bills'),
    requirePerm: Perm.BILL_VIEW,
    uiFeatureKey: 'bills',
  },
  {
    to: routes.private.purchaseOrders,
    icon: IconTruckDelivery,
    label: i18n.t('common:navigation.purchaseOrders'),
    requirePerm: Perm.PURCHASE_ORDER_VIEW,
    uiFeatureKey: 'purchase_orders',
  },
  {
    to: routes.private.rfqs,
    icon: IconFileDescription,
    label: i18n.t('common:navigation.rfq'),
    requirePerm: Perm.RFQ_VIEW,
    uiFeatureKey: 'rfqs',
  },
  {
    to: generatePath(routes.private.contacts, { contactParty: ContactParty.vendor }),
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.vendors'),
    requirePerm: Perm.VENDOR_VIEW,
    uiFeatureKey: 'vendors',
  },
]
export const CONTRACT_ITEMS: NavItem[] = [
  {
    to: routes.private.contracts,
    icon: IconNotes,
    label: i18n.t('common:navigation.contracts'),
    feature: Feature.contractManagement,
    requirePerm: Perm.CONTRACT_VIEW,
    uiFeatureKey: 'contracts',
  },
]

export const ORGANIZATION_MENU: NavItem[] = [
  {
    to: routes.private.products,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.products'),
    requirePerm: Perm.PRODUCT_VIEW,
    uiFeatureKey: 'products',
  },
  {
    to: routes.private.accounts,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.accounts'),
    requirePerm: Perm.CHART_OF_ACCOUNTS_VIEW,
    uiFeatureKey: 'accounts',
  },
  {
    to: routes.private.accountCategories,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.categories'),
    requirePerm: Perm.CATEGORY_VIEW,
    uiFeatureKey: 'categories',
  },
  {
    to: routes.private.taxes,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.taxes'),
    requirePerm: Perm.TAX_VIEW,
    uiFeatureKey: 'taxes',
  },
  {
    to: routes.private.companyDetails,
    icon: IconBuildingWarehouse,
    label: i18n.t('common:navigation.companyDetails'),
    // Migrate the last legacy `show: [Role.admin]` to a real perm.
    // The page edits team-wide accounting/company info — same gate
    // as the route guard in Routes/index.tsx.
    requirePerm: Perm.ACCOUNTING_MANAGE_SETTINGS,
  },
]
// HEAD ITEMS

export const QUICK_ACTIONS_ITEMS: NavItem[] = [
  {
    to: routes.private.approvalRequest,
    label: i18n.t('common:navigation.approvalRequest'),
    icon: IconCircleCheck,
    // The destination route is `requirePerm={Perm.REQUEST_ADD}` —
    // mirror it here so the "+" quick-action menu doesn't surface
    // an entry that redirects to landing.
    requirePerm: Perm.REQUEST_ADD,
  },
  {
    to: routes.private.taskBoards,
    label: i18n.t('common:navigation.task'),
    icon: IconListDetails,
    requirePerm: Perm.TASK_VIEW,
    uiFeatureKey: 'tasks',
  },
  {
    to: generatePath(routes.private.pipelineById, { id: 'new' }),
    label: i18n.t('common:navigation.workflow'),
    icon: IconBolt,
    requirePerm: Perm.WORKFLOW_ADD,
    uiFeatureKey: 'workflows',
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.vendor }),
    label: i18n.t('common:navigation.vendor'),
    uiFeatureKey: 'vendors',
    icon: IconBuildingWarehouse,
    // CREATE quick-action: backend POST on Collaboration is gated on
    // accounting.manage (PerMethodPerms in accounting/views/
    // collaboration.py). Surfacing this for users with only
    // vendor.view would 403 on submit.
    requirePerm: Perm.ACCOUNTING_MANAGE,
  },
  {
    to: generatePath(routes.private.contact, { id: 'new', contactParty: ContactParty.customer }),
    label: i18n.t('common:navigation.customer'),
    uiFeatureKey: 'customers',
    icon: IconUsers,
    requirePerm: Perm.ACCOUNTING_MANAGE,
  },
]

const openSuggestFeature = () => {
  // Intercom is the low-friction path; if the widget hasn't loaded, fall back to email.
  if (isIntercomAvailable()) {
    safeIntercom('showNewMessage', i18n.t('common:suggestFeature.intercomPrefill'))
  } else {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      String(i18n.t('common:suggestFeature.emailSubject')),
    )}`
  }
}

export const useGetResourceItems = () => {
  // `t` from useTranslation is reactive to language switches (unlike the
  // module-level i18n.t used for the static nav arrays above), so the memo
  // recomputes and the menu labels stay in the active locale.
  const { t } = useTranslation('common', { keyPrefix: 'navigation' })
  return useMemo(
    () => [
      { to: GUIDES_LINK, label: t('allGuides'), external: true },
      { to: YOUTUBE_PLAYLISTS, label: t('videoGuides'), external: true },
      { to: CALL_LINK, label: t('bookCall'), external: true },
      { label: t('suggestFeature'), onClick: openSuggestFeature },
    ],
    [t],
  )
}
